import React, {Suspense} from 'react';
import {Link} from 'react-router-dom';
import {UseQueryResult} from '@tanstack/react-query';
import {ListConfigContext} from '@src/contexts/ListConfigContext';
import {UserContext} from '@src/contexts/UserContext';
import {
    ListConfigNamedColumn,
    ListPaginationHttpResponse,
    ListProps,
    ListSortSettings,
    ListState
} from '@src/@types/components/List';
import {IListConfigContext} from '@src/@interfaces/contexts/IListConfigContext';
import ListRecordsProvider from './ListRecordsProvider';
import {IEmptyModel} from '@src/@interfaces/IEmptyModel';
import app from '@src/helpers/app';
import {IInteractiveListConfig} from '@src/@interfaces/services/ConfigMaker/IInteractiveListConfig';
import Translate from '@src/components/Translate/Translate';
import {IUser} from '@src/@interfaces/services/Auth/IUser';
import {debounce} from '@src/helpers/debounce-throttle';
import {UserPreferencesKeys} from '@src/@types/services/UserPreferencesNamesResolver';
import ListContent from './ListContent';
import Cache from '@src/classes/Cacheable/Cache';
import {UserPreferenceEvents} from '@src/@types/services/Auth/UserPreferenceEvents';
import classnames from '@src/helpers/classnames';
import LoadingIndicator from '@src/components/LoadingIndicator/LoadingIndicator';
import {withRouterApi} from '@src/HOCs/withRouterApi';
import {WithRouterApiProps} from '@src/@types/hocs/WithRouterApiProps';
import queryClient from '@src/react-query-client';
import {shortCode} from '@src/helpers/hash/shortCode';

const Filters = React.memo(
        React.lazy(() => import('./ListFilters/ListFilters'))
);

type Props<Model extends IEmptyModel = IEmptyModel> = WithRouterApiProps & ListProps<Model>;

class List<
        Model extends IEmptyModel = IEmptyModel
> extends React.Component<Props<Model>, ListState> {
    static contextType = UserContext;
    context!: React.ContextType<typeof UserContext>;

    state: ListState = {
        filtersDisplayed: false,
        disableFiltering: false,
        currentPage: 1,
    };

    protected cache = new Cache<any>();

    protected user: IUser | null;
    protected debouncedOnSortingParamsChanged;

    constructor(props: Props<Model>) {
        super(props);

        this.setPageNumber = this.setPageNumber.bind(this);
        this.toggleFiltersVisibility = this.toggleFiltersVisibility.bind(this);
        this.debouncedOnSortingParamsChanged = debounce(this.onSortingParamsChanged.bind(this), 300);
        this.onUserPreferencesChanged = this.onUserPreferencesChanged.bind(this);
        this.onSearchTermChanged = this.onSearchTermChanged.bind(this);
        this.onRowClicked = this.onRowClicked.bind(this);
        this.renderCreateButton = this.renderCreateButton.bind(this);
        this.renderContent = this.renderContent.bind(this);
        this.canOpenModelShowPage = this.canOpenModelShowPage.bind(this);
        this.enableFiltering = this.enableFiltering.bind(this);

        this.user = app('auth').getUser();
        this.state = {
            ...this.state,
            ...this.getStateFromUserPreferencesToUpdate(),
        };
    }

    componentDidMount() {
        if (super.componentDidMount) {
            super.componentDidMount();
        }

        app('events').listen(UserPreferenceEvents.SAVED, this.onUserPreferencesChanged);
    }

    componentDidUpdate(prevProps: Readonly<Props<Model>>, prevState: Readonly<ListState>, snapshot?: any) {
        if (super.componentDidUpdate) {
            super.componentDidUpdate(prevProps, prevState, snapshot);
        }

        if (prevProps.controllerName !== this.props.controllerName) {
            this.flushQueryCache();
            this.setState(this.getStateFromUserPreferencesToUpdate());
        }

        const names: Array<keyof Props> = [
            'dontChangeUserPreferences',
            'defaultDisplayFilters',
            'forceColumnsValues',
        ];

        if (names.some(name => prevProps[name] !== this.props[name])) {
            this.setState({
                filtersDisplayed: this.getFiltersDisplayedStateToUpdate(),
            });
        }

        // TODO: rework lines below
        const [params] = this.props.urlSearchParams;
        const page = this.state.currentPage;
        const urlPageNumber = +(params.get(this.getPageNumberGetParamName()) ?? 1);
        if (page != null && page !== urlPageNumber) {
            this.setPageNumber(urlPageNumber);
        }

        const searchTerm = this.state.searchTerm;
        const urlSearchTerm = params.get(this.getSearchTermGetParamName());
        if (searchTerm !== urlSearchTerm) {
            this.setSearchTerm(urlSearchTerm);
        }

        const disableFiltering = this.state.disableFiltering;
        const urlDisableFiltering = this.getDisableFilteringValueFromSearchParams(params);
        if (disableFiltering !== urlDisableFiltering) {
            this.setDisableFiltering(urlDisableFiltering);
        }
    }

    componentWillUnmount() {
        if (super.componentWillUnmount) {
            super.componentWillUnmount();
        }

        this.debouncedOnSortingParamsChanged.cancel();
        this.flushQueryCache();

        app('events').forget(UserPreferenceEvents.SAVED, this.onUserPreferencesChanged);
    }

    render() {
        return <ListConfigContext.Provider value={this.getListConfigContextValue()}>
            <ListRecordsProvider<Model>
                    uniqueListName={this.props.uniqueListName}
                    currentPage={this.state.currentPage ?? 1}
                    requestUri={this.props.getListRequestUrl ?? this.config.getUrlOfRecordsListEndpoint()}
                    setCurrentPageNumber={this.setPageNumber}
                    searchTerm={this.state.searchTerm}
                    sortBy={this.state.sortBy}
                    queryKeyAdditionalShapeProps={this.props.recordsProviderAdditionalShapeProps}
                    listenEventsToRefreshList={this.props.config.listenEventsToRefreshList}
                    disableFiltering={this.state.disableFiltering}
                    chosenFilterId={this.props.chosenFilterId}
                    chosenFilterName={this.props.chosenFilterName}
            >
                {this.renderContent}
            </ListRecordsProvider>
        </ListConfigContext.Provider>;
    }

    protected renderContent(queryResult: UseQueryResult<ListPaginationHttpResponse<Model>, Error>) {
        const filtersDisplayed = (
                this.config.hasFilterableColumns() &&
                this.state.filtersDisplayed &&
                !this.state.disableFiltering
        );

        return <div className={classnames('list-view', {
            'list-view--filters-displayed': filtersDisplayed,
        })}>
            {filtersDisplayed ? <div className="list-view__filters">
                <Suspense
                        fallback={<div className="py-4 text-center"><LoadingIndicator type="border" size="sm"/></div>}
                >
                    <Filters
                            allowedFilters={queryResult.data?.allowedFilters}
                            refreshList={queryResult.refetch}
                            toggleFiltersVisibility={this.toggleFiltersVisibility}
                            preferenceNameMutator={this.props.tableHeadProps?.preferenceNameMutator}
                            chosenFilterId={this.props.chosenFilterId}
                            chosenFilterName={this.props.chosenFilterName}
                            dontChangeUserPreferences={this.props.dontChangeUserPreferences}
                            disabledColumns={this.props.disabledColumns}
                            forceColumnsValues={this.props.forceColumnsValues}
                            isFetching={queryResult.isFetching || queryResult.isLoading}
                    />
                </Suspense>
            </div> : null}

            <div className="list-view__content">
                <ListContent
                        {...this.props}
                        queryResult={queryResult}
                        sortBy={this.state.sortBy}
                        searchTerm={this.state.searchTerm}
                        saveSearchTerm={this.onSearchTermChanged}
                        filtersAreVisible={this.state.filtersDisplayed}
                        toggleFiltersVisibility={this.toggleFiltersVisibility}
                        createButton={this.renderCreateButton}
                        canOpenModelShowPage={this.canOpenModelShowPage}
                        saveSortingParams={this.debouncedOnSortingParamsChanged}
                        onRowClicked={this.props.onRowClicked ? this.onRowClicked : undefined}
                        tableProps={this.props.tableProps}
                        tableHeadProps={this.props.tableHeadProps}
                        tableBodyProps={this.props.tableBodyProps}
                        tableCaptionNode={this.props.tableCaptionNode}
                        isIsolatedComponent={this.props.isIsolatedComponent}
                        uniqueListName={this.props.uniqueListName}
                        listSettingsControllerName={this.props.controllerName}
                        pageNumberGetParamName={this.getPageNumberGetParamName()}
                        filteringDisabled={this.state.disableFiltering}
                        enableFiltering={this.enableFiltering}
                        renderComponentBeforeTheTopPagination={this.props.renderComponentBeforeTheTopPagination}
                        renderComponentAfterTheTopPagination={this.props.renderComponentAfterTheTopPagination}
                        renderComponentBeforeTheBottomPagination={this.props.renderComponentBeforeTheBottomPagination}
                        renderComponentAfterTheBottomPagination={this.props.renderComponentAfterTheBottomPagination}
                        chosenFilterName={this.props.chosenFilterName}
                        chosenFilterId={this.props.chosenFilterId}
                />
            </div>
        </div>;
    }

    protected renderCreateButton() {
        const url = this.getModelCreatePageUrl();
        if (!this.canOpenModelCreatePage() || !url) {
            return null;
        }

        return <Link to={url} className="models-list__create-button btn-sm btn-label">
            <i className="fa fa-plus label-icon"/>
            <Translate message="List.create-button-label"/>
        </Link>;
    }

    protected getStateFromUserPreferencesToUpdate(): ListState {
        const getReference = (key: UserPreferencesKeys) => {
            return this.callWithPreferencesNameResolver(() => {
                return this.user?.getModelPreferenceValue(this.props.controllerName, key);
            });
        };

        const [params] = this.props.urlSearchParams;
        const currentPage = +(params.get(this.getPageNumberGetParamName()) ?? 1);
        const searchTerm = params.get(this.getSearchTermGetParamName());
        const disableFiltering = this.getDisableFilteringValueFromSearchParams(params);

        return {
            currentPage,
            searchTerm,
            sortBy: getReference(UserPreferencesKeys.SORT_BY),
            filtersDisplayed: this.getFiltersDisplayedStateToUpdate(),
            disableFiltering,
        };
    }

    protected getFiltersDisplayedStateToUpdate() {
        if (this.props.forceDisplayFilters) {
            return this.props.forceDisplayFilters;
        }

        if (this.props.dontChangeUserPreferences && this.props.defaultDisplayFilters) {
            return this.props.defaultDisplayFilters;
        }

        return this.callWithPreferencesNameResolver(() => {
            return this.user?.getModelPreferenceValue(
                    this.props.controllerName,
                    UserPreferencesKeys.LIST_FILTERS_VISIBILITY
            );
        }) ?? this.props.defaultDisplayFilters ?? false;
    }

    protected onUserPreferencesChanged() {
        this.cache.forgetRemember(this.getColumnsCacheKey());
    }

    protected onSearchTermChanged(searchTerm?: string | null) {
        this.setSearchParams({
            [this.getPageNumberGetParamName()]: '1',
            [this.getSearchTermGetParamName()]: searchTerm ?? '',
        }, true);
        this.setState({searchTerm, currentPage: 1});
    }

    protected onSortingParamsChanged(sortBy?: ListSortSettings) {
        if (!this.props.dontChangeUserPreferences) {
            this.callWithPreferencesNameResolver(() => {
                this.user?.setModelPreferenceValue(this.props.controllerName, UserPreferencesKeys.SORT_BY, sortBy);
            });
        }

        this.setState({sortBy});
    }

    protected getListConfigContextValue(): IListConfigContext<Model> {
        const that = this;
        const {
            columns = null,
            columnsNumber = null,
            ...contextOptions
        } = this.props.listConfigContextOptions ?? {};

        const controlsColumnsNumber = React.Children.count(this.props?.tableHeadProps?.cellBeforeColumns) + 1;

        return {
            controllerName: this.props.controllerName,
            config: this.props.config,
            get columns() {
                return columns ?? that.getColumns();
            },
            get columnsNumber() {
                // +1 because list always renders list settings button
                return columnsNumber ?? (that.getColumns().length + controlsColumnsNumber);
            },
            currentPage: this.state.currentPage ?? 1,
            setCurrentPage: this.setPageNumber,
            ...(contextOptions ?? {}),
        };
    }

    protected getColumnsCacheKey() {
        return this.props.controllerName + '@columns';
    }

    protected getColumns(): ListConfigNamedColumn[] {
        if (!this.props.config) {
            return [];
        }

        return this.cache.remember(this.getColumnsCacheKey(), () => {
            return this.callWithPreferencesNameResolver(
                    () => this.config.getPreparedColumnsList(
                            this.props.config.columns,
                            this.user?.getModelPreferenceValue(
                                    this.props.controllerName,
                                    UserPreferencesKeys.LIST_COLUMNS_VISIBILITY
                            ),
                            this.user?.getModelPreferenceValue(
                                    this.props.controllerName,
                                    UserPreferencesKeys.LIST_COLUMNS_ORDER
                            )
                    )
            );
        });
    }

    protected toggleFiltersVisibility(state: boolean | undefined) {
        const filtersDisplayed = state == null ? !this.state.filtersDisplayed : state;

        if (!this.props.dontChangeUserPreferences) {
            this.callWithPreferencesNameResolver(() => {
                this.user?.saveModelPreferenceValue(
                        this.props.controllerName,
                        UserPreferencesKeys.LIST_FILTERS_VISIBILITY,
                        filtersDisplayed
                );
            });
        }

        this.setState({filtersDisplayed});
    }

    protected setPageNumber(number: number): void {
        this.setPageNumberInURLSearchParams(number);
        this.setState({currentPage: number});
    }

    protected setSearchTerm(searchTerm: string | null | undefined): void {
        this.setSearchTermInURLSearchParams(searchTerm);
        this.setState({searchTerm});
    }

    protected setDisableFiltering(disableFiltering: boolean): void {
        this.setDisableFilteringInURLSearchParams(disableFiltering);
        this.setState({disableFiltering});
    }

    protected canOpenModelCreatePage(): boolean {
        return this.canOpenModelPage('create');
    }

    protected canOpenModelShowPage(): boolean {
        return this.canOpenModelPage('update');
    }

    protected getModelCreatePageUrl(): string {
        return this.config.getUrlOfModelCreatePage();
    }

    protected onRowClicked(index: number, model: Model, isMiddleButton: boolean) {
        if (this.props.onRowClicked) {
            this.props.onRowClicked(index, model, isMiddleButton, {
                location: this.props.urlLocation,
                searchParams: this.props.urlSearchParams[0],
            });
        }
    }

    protected flushQueryCache(): void {
        const key = app('queryKeys').getCustomListOfExactModelRecordsQueryKey(
                this.props.controllerName, undefined, 'filters'
        );

        // setTimeout used to avoid state changes in useQuery after component unmount
        setTimeout(() => {
            queryClient.invalidateQueries({queryKey: key, exact: true}).then();
        }, 10);
    }

    protected callWithPreferencesNameResolver<T>(callback: () => T): T {
        return app('names-resolver').userPreferences.withNameMutator(
                this.props.tableHeadProps?.preferenceNameMutator,
                callback
        );
    }

    protected get config(): IInteractiveListConfig {
        return app('config-maker').makeListConfig(this.props.controllerName, this.props.config);
    }

    protected getPageNumberGetParamName(): string {
        return shortCode(
                app('names-resolver')
                        .listNames
                        .getPageNumberGetParamNameByListUniqueName(this.props.uniqueListName)
        );
    }

    protected getSearchTermGetParamName(): string {
        return shortCode(
                app('names-resolver')
                        .listNames
                        .getSearchTermGetParamNameByListUniqueName(this.props.uniqueListName)
        );
    }

    protected getDisableFilteringGetParamName(): string {
        return app('names-resolver')
                .listNames
                .getDisableFilteringGetParamNameByListUniqueName(this.props.uniqueListName);
    }

    private getDisableFilteringValueFromSearchParams(searchParams: URLSearchParams): boolean {
        const key = this.getDisableFilteringGetParamName();

        return searchParams.has(key) && !['false', '0', 'no'].includes(searchParams.get(key) as any);
    }

    private canOpenModelPage(context: 'create' | 'update'): boolean {
        const permissions = this.props.config[context]?.permissions ?? this.guessModelPagePermissions(context);

        return !permissions || this.context != null && this.context.canAny(permissions);
    }

    private guessModelPagePermissions(context: 'create' | 'update'): string | string[] {
        const resolver = app('names-resolver').permissions;
        if (context === 'create') {
            return resolver.getCreateRoutePermissionName(this.props.controllerName);
        }

        return [
            resolver.getShowRoutePermissionName(this.props.controllerName),
            resolver.getUpdateRoutePermissionName(this.props.controllerName),
        ];
    }

    private enableFiltering() {
        this.setDisableFilteringInURLSearchParams(false);
    }

    private setDisableFilteringInURLSearchParams(state: boolean) {
        const searchParams = this.getModifiedSearchParams();
        if (state) {
            searchParams.append(this.getDisableFilteringGetParamName(), 'true');
        } else {
            searchParams.delete(this.getSearchTermGetParamName());
            searchParams.delete(this.getDisableFilteringGetParamName());
        }

        this.setSearchParams(searchParams);
    }

    private setPageNumberInURLSearchParams(pageNumber: number) {
        this.setSearchParams({
            [this.getPageNumberGetParamName()]: String(pageNumber)
        }, true);
    }

    private setSearchTermInURLSearchParams(searchTerm: string | null | undefined) {
        this.setSearchParams({
            [this.getSearchTermGetParamName()]: searchTerm ?? ''
        }, true);
    }

    private setSearchParams(params: URLSearchParams | Record<string, string>, replace = true) {
        this.props.urlNavigate({
            ...this.props.urlLocation,
            search: (
                    params instanceof URLSearchParams ?
                            params :
                            this.getModifiedSearchParams(params)
            ).toString(),
        }, {replace});
    }

    private getModifiedSearchParams(params?: Record<string, string>): URLSearchParams {
        const searchParams = new URLSearchParams(this.props.urlLocation.search);
        if (params) {
            Object.keys(params).forEach(key => {
                searchParams.set(key, params[key]);
            });
        }

        return searchParams;
    }
}

function WrappedList<Model extends IEmptyModel = IEmptyModel>(props: ListProps<Model>) {
    const Component = withRouterApi<Props<Model>>(List<Model>);

    return <Component {...props}/>;
}

export default WrappedList;
